import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Card,
    CardBody,
    Container,
    Flex,
    Heading,
    HStack,
    Icon,
    SimpleGrid,
    Spinner,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
    VStack,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Cell,
} from 'recharts';
import {
    FaChartLine,
    FaPhone,
    FaUserCheck,
    FaUsers,
    FaUserTie,
    FaExclamationTriangle,
} from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import SyntheticPreviewBanner from '../../compnents/SyntheticPreviewBanner';
import { BookImpactView } from '../../compnents/BookImpactPanel';
import { rmCoverageRows, urgencyTotalsFromQueue } from '../../utils/urgencyTotals';
import {
    buildSyntheticActions,
    buildSyntheticWhoToCall,
    demoCustomers,
    demoInsights,
    demoPlatformImpact,
    synthesizeRmsFromCustomers,
} from '../../content/demoAnalytics';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const SEGMENT_COLORS = ['#0B2545', '#38A169', '#805AD5', '#DD6B20', '#E53E3E', '#D69E2E', '#00B5D8', '#D53F8C'];
const PIPELINE_COLORS = ['#0B2545', '#38A169', '#DD6B20', '#805AD5'];
const STATUS_COLORS = ['#38A169', '#DD6B20', '#805AD5', '#0B2545'];

const toDateKey = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
};

const buildRecentActivityData = (actions) => {
    const buckets = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let index = 6; index >= 0; index -= 1) {
        const date = new Date(today);
        date.setDate(today.getDate() - index);
        const key = date.toISOString().slice(0, 10);
        buckets.push({
            key,
            name: `${date.getDate()}/${date.getMonth() + 1}`,
            logged: 0,
            completed: 0,
            deferred: 0,
        });
    }

    const bucketMap = buckets.reduce((acc, item) => {
        acc[item.key] = item;
        return acc;
    }, {});

    actions.forEach((action) => {
        const createdKey = toDateKey(action.created_at);
        const updatedKey = toDateKey(action.updated_at || action.created_at);

        if (createdKey && bucketMap[createdKey]) {
            bucketMap[createdKey].logged += 1;
        }

        if (action.status === 'done' && updatedKey && bucketMap[updatedKey]) {
            bucketMap[updatedKey].completed += 1;
        }

        if (action.status === 'snoozed' && updatedKey && bucketMap[updatedKey]) {
            bucketMap[updatedKey].deferred += 1;
        }
    });

    return buckets;
};

const isWithinLastDays = (value, days) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;

    const threshold = new Date();
    threshold.setHours(0, 0, 0, 0);
    threshold.setDate(threshold.getDate() - (days - 1));

    return date >= threshold;
};

const toPercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
};

function DashboardAdminPlatformImpact() {
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [impactData, setImpactData] = useState(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [bookImpact, setBookImpact] = useState(null);

    const fetchPlatformImpact = async () => {
        // Start the euro panel with the page data instead of after it: on a
        // 10,000-client book it takes ~4 s and was the last step to load.
        const bookImpactRequest = axios.get(`${API_BASE_URL}/content/product-data/book-impact`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((response) => response.data).catch(() => null);
        bookImpactRequest.then(setBookImpact);
        try {
            setLoading(true);

            const [insightsRes, customersRes, rmsRes, actionsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/content/admin-customer-insights`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${API_BASE_URL}/content/list-customers`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { page: 1, page_size: 200 },
                }),
                axios.get(`${API_BASE_URL}/content/list-rms`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${API_BASE_URL}/content/rm-actions`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            let insights = insightsRes.data || {};
            const customers = customersRes.data?.customers || [];
            // The customer list is one page; the book size comes from the engine.
            const bookSize = Number(customersRes.data?.pagination?.total_count) || customers.length;
            const realRms = (rmsRes.data?.rms || []).length > 0;
            let rms = rmsRes.data?.rms || [];
            let actions = Array.isArray(actionsRes.data) ? actionsRes.data : [];

            if (customers.length === 0 && (!insights.segments || insights.segments.length === 0)) {
                setImpactData(demoPlatformImpact);
                setLastUpdatedAt(new Date());
                return;
            }

            insights = {
                ...demoInsights,
                ...insights,
                segments: Array.isArray(insights.segments) && insights.segments.length > 0 ? insights.segments : demoInsights.segments,
                bias_ranking: Array.isArray(insights.bias_ranking) && insights.bias_ranking.length > 0 ? insights.bias_ranking : demoInsights.bias_ranking,
            };

            if (rms.length === 0) {
                rms = synthesizeRmsFromCustomers(customers);
            }

            const actionsAreSynthetic = actions.length === 0;
            if (actionsAreSynthetic) {
                actions = buildSyntheticActions(customers.length ? customers : demoCustomers);
            }
            const customerRmMap = customers.reduce((acc, customer) => {
                acc[customer.customer_id] = customer.rm_id;
                return acc;
            }, {});
            const rmNameMap = rms.reduce((acc, rm) => {
                acc[rm.rm_id] = rm.full_name || rm.email || rm.rm_id;
                return acc;
            }, {});

            const whoToCallByRm = await Promise.all(
                rms.map(async (rm) => {
                    const assignedCustomersForRm = customers.filter((customer) => customer.rm_id === rm.rm_id);
                    const shouldUseSyntheticQueue = rm.rm_id?.startsWith?.('rm-demo') || assignedCustomersForRm.length === 0;

                    if (shouldUseSyntheticQueue) {
                        return {
                            rm,
                            whoToCall: buildSyntheticWhoToCall(assignedCustomersForRm.length ? assignedCustomersForRm : customers).slice(0, 20),
                        };
                    }

                    try {
                        const response = await axios.post(
                            `${API_BASE_URL}/content/admin-who-to-call-for-a-rm`,
                            // The top 20 carry the urgency basis for book-level counts.
                            { rm_id: rm.rm_id, limit: 20 },
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        );

                        return {
                            rm,
                            whoToCall: Array.isArray(response.data) ? response.data : [],
                        };
                    } catch (error) {
                        return {
                            rm,
                            whoToCall: buildSyntheticWhoToCall(assignedCustomersForRm).slice(0, 20),
                        };
                    }
                })
            );

            // With real advisers, assignment comes from their customer counts (the
            // page above holds only the first 200 customers).
            const assignedCustomers = realRms
                ? rms.reduce((sum, rm) => sum + Number(rm.customer_count || 0), 0)
                : customers.filter((customer) => customer.rm_id).length;
            const highRiskCustomers = customers.filter((customer) => Number(customer.risk_profile || 0) >= 8).length;
            const avgRiskProfile = customers.length > 0
                ? customers.reduce((sum, customer) => sum + Number(customer.risk_profile || 0), 0) / customers.length
                : 0;

            const actionableContactsFromRm = whoToCallByRm.flatMap((item) => item.whoToCall);
            const actionableContactsRaw = actionableContactsFromRm.length > 0
                ? actionableContactsFromRm
                : buildSyntheticWhoToCall(customers.length ? customers : demoCustomers).slice(0, 20);
            // The same customer can appear in several RM queues (e.g. while no
            // RM assignment exists every queue is built from the full base), so
            // dedupe by customer before counting qualified/urgent contacts.
            const actionableContacts = Object.values(actionableContactsRaw.reduce((acc, item) => {
                const key = item.customer_id || item.id;
                if (!key) return acc;
                if (!acc[key] || Number(item.priority_score || 0) > Number(acc[key].priority_score || 0)) {
                    acc[key] = item;
                }
                return acc;
            }, {}));
            // Urgency is relative to each adviser's book (engine policy priority-2026.10):
            // HIGH is the top 5%, MEDIUM the next 15%.
            // Book-level counts per adviser, derived exactly from the engine's
            // urgency basis; lists only hold each adviser's top 20.
            const bookTotals = whoToCallByRm.map((item) => urgencyTotalsFromQueue(item.whoToCall));
            const hasBookTotals = bookTotals.length > 0 && bookTotals.every(Boolean);
            const qualifiedContacts = actionableContacts.filter((item) => {
                const urgency = (item.urgency_level || '').toLowerCase();
                return urgency === 'high' || urgency === 'medium';
            });
            const urgentCustomers = actionableContacts.filter((item) => {
                const urgency = (item.urgency_level || '').toLowerCase();
                return urgency === 'high' || urgency === 'urgent';
            });

            const trackedCustomers = new Set(actions.map((action) => action.customer_id)).size;
            const appointmentsLogged = actions.filter((action) => action.action_type === 'meeting').length;
            const completedActions = actions.filter((action) => action.status === 'done').length;
            const snoozedActions = actions.filter((action) => action.status === 'snoozed').length;
            const openActions = actions.filter((action) => (action.status || 'open') === 'open').length;
            const actionableCustomerIds = new Set(actionableContacts.map((item) => item.customer_id));

            const weeklyLoggedActions = actions.filter((action) => isWithinLastDays(action.created_at, 7));
            const weeklyCompletedActions = actions.filter(
                (action) => action.status === 'done' && isWithinLastDays(action.updated_at || action.created_at, 7)
            );
            const weeklyDeferredActions = actions.filter(
                (action) => action.status === 'snoozed' && isWithinLastDays(action.updated_at || action.created_at, 7)
            );
            const weeklyMeetingActions = actions.filter(
                (action) => action.action_type === 'meeting' && isWithinLastDays(action.created_at, 7)
            );
            const weeklyTouchedCustomerIds = new Set(
                actions
                    .filter((action) => isWithinLastDays(action.created_at, 7) || isWithinLastDays(action.updated_at || action.created_at, 7))
                    .map((action) => action.customer_id)
            );
            const weeklyQualifiedTouched = Array.from(weeklyTouchedCustomerIds)
                .filter((customerId) => actionableCustomerIds.has(customerId))
                .length;
            const overdueFollowUps = actions.filter((action) => {
                if (action.status !== 'snoozed' || !action.next_contact_date) return false;
                const nextContactDate = new Date(action.next_contact_date);
                return !Number.isNaN(nextContactDate.getTime()) && nextContactDate.getTime() <= Date.now();
            }).length;
            const weeklyActiveRms = new Set(
                actions
                    .filter((action) => isWithinLastDays(action.created_at, 7) || isWithinLastDays(action.updated_at || action.created_at, 7))
                    .map((action) => customerRmMap[action.customer_id])
                    .filter(Boolean)
            ).size;

            const responseCycleCandidates = actions
                .map((action) => {
                    const createdAt = new Date(action.created_at);
                    const updatedAt = new Date(action.updated_at || action.created_at);
                    const diff = updatedAt.getTime() - createdAt.getTime();
                    return diff > 0 ? diff / (1000 * 60 * 60 * 24) : null;
                })
                .filter((value) => value !== null);

            const avgResponseCycleDays = responseCycleCandidates.length > 0
                ? responseCycleCandidates.reduce((sum, value) => sum + value, 0) / responseCycleCandidates.length
                : 0;

            const segmentDistribution = (insights.segments || [])
                .sort((a, b) => b.count - a.count)
                .slice(0, 6)
                .map((segment, index) => ({
                    name: segment.segment,
                    value: segment.count,
                    percentage: segment.percentage,
                    color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
                }));

            const pipelineData = [
                { name: 'Qualified', value: qualifiedContacts.length, color: PIPELINE_COLORS[0] },
                { name: 'Tracked', value: trackedCustomers, color: PIPELINE_COLORS[1] },
                { name: 'Completed', value: completedActions, color: PIPELINE_COLORS[2] },
                { name: 'Deferred', value: snoozedActions, color: PIPELINE_COLORS[3] },
            ];

            const recentActivityData = buildRecentActivityData(actions);
            const actionStatusData = [
                { name: 'Completed', value: completedActions, color: STATUS_COLORS[0] },
                { name: 'Deferred', value: snoozedActions, color: STATUS_COLORS[1] },
                { name: 'Appointments', value: appointmentsLogged, color: STATUS_COLORS[2] },
                { name: 'Open', value: openActions, color: STATUS_COLORS[3] },
            ];

            const rmCoverage = rmCoverageRows(whoToCallByRm, customers);

            const workflowAccumulator = actions.reduce((acc, action) => {
                const rmId = customerRmMap[action.customer_id];
                const rmName = rmNameMap[rmId] || 'Unassigned RM';

                if (!acc[rmName]) {
                    acc[rmName] = {
                        name: rmName,
                        tracked: 0,
                        completed: 0,
                        deferred: 0,
                        meetings: 0,
                        overdue: 0,
                        weeklyLogged: 0,
                        weeklyCompleted: 0,
                        weeklyMeetings: 0,
                        weeklyDeferred: 0,
                    };
                }

                acc[rmName].tracked += 1;
                if (action.action_type === 'meeting') acc[rmName].meetings += 1;
                if (action.status === 'done') acc[rmName].completed += 1;
                if (action.status === 'snoozed') acc[rmName].deferred += 1;

                if (action.status === 'snoozed' && action.next_contact_date) {
                    const nextContactDate = new Date(action.next_contact_date);
                    if (!Number.isNaN(nextContactDate.getTime()) && nextContactDate.getTime() <= Date.now()) {
                        acc[rmName].overdue += 1;
                    }
                }

                if (isWithinLastDays(action.created_at, 7)) {
                    acc[rmName].weeklyLogged += 1;
                    if (action.action_type === 'meeting') acc[rmName].weeklyMeetings += 1;
                }

                if (action.status === 'done' && isWithinLastDays(action.updated_at || action.created_at, 7)) {
                    acc[rmName].weeklyCompleted += 1;
                }

                if (action.status === 'snoozed' && isWithinLastDays(action.updated_at || action.created_at, 7)) {
                    acc[rmName].weeklyDeferred += 1;
                }

                return acc;
            }, {});

            const workflowByRm = Object.values(workflowAccumulator)
                .map((rm) => ({
                    ...rm,
                    completionRate: toPercentage(rm.completed, rm.tracked),
                    weeklyCompletionRate: toPercentage(rm.weeklyCompleted, rm.weeklyLogged),
                }))
                .sort((a, b) => b.tracked - a.tracked)
                .slice(0, 5);

            const weeklyWorkflowByRm = Object.values(workflowAccumulator)
                .filter((rm) => rm.weeklyLogged > 0 || rm.weeklyCompleted > 0 || rm.weeklyMeetings > 0 || rm.overdue > 0)
                .map((rm) => ({
                    ...rm,
                    weeklyCompletionRate: toPercentage(rm.weeklyCompleted, rm.weeklyLogged),
                }))
                .sort((a, b) => {
                    const scoreA = a.weeklyLogged + a.weeklyCompleted + a.weeklyMeetings;
                    const scoreB = b.weeklyLogged + b.weeklyCompleted + b.weeklyMeetings;
                    return scoreB - scoreA;
                })
                .slice(0, 6);

            setImpactData({
                totalCustomers: bookSize,
                assignedCustomers,
                avgRiskProfile,
                qualifiedContacts: hasBookTotals
                    ? bookTotals.reduce((sum, totals) => sum + totals.high + totals.medium, 0)
                    : qualifiedContacts.length,
                highRiskCustomers,
                urgentCustomers: hasBookTotals
                    ? bookTotals.reduce((sum, totals) => sum + totals.high, 0)
                    : urgentCustomers.length,
                trackedCustomers,
                appointmentsLogged,
                completedActions,
                avgResponseCycleDays,
                segmentDistribution,
                pipelineData,
                rmCoverage,
                recentActivityData,
                actionStatusData,
                workflowByRm,
                weeklySummary: {
                    touchedCustomers: weeklyTouchedCustomerIds.size,
                    coverageRate: toPercentage(weeklyQualifiedTouched, actionableCustomerIds.size),
                    completedActions: weeklyCompletedActions.length,
                    completionRate: toPercentage(weeklyCompletedActions.length, weeklyLoggedActions.length),
                    meetingsBooked: weeklyMeetingActions.length,
                    meetingRate: toPercentage(weeklyMeetingActions.length, weeklyLoggedActions.length),
                    deferredActions: weeklyDeferredActions.length,
                    overdueFollowUps,
                    openBacklog: openActions,
                    activeRms: weeklyActiveRms,
                },
                weeklyWorkflowByRm,
                actionsAreSynthetic,
            });
            setLastUpdatedAt(new Date());
        } catch (error) {
            console.error('Platform impact fetch error:', error);
            setImpactData(demoPlatformImpact);
            setLastUpdatedAt(new Date());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlatformImpact();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <Flex justify="center" align="center" py={20}>
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.teal" thickness="4px" />
                    <Text color="gray.600">Loading platform impact...</Text>
                </VStack>
            </Flex>
        );
    }

    if (!impactData) {
        return (
            <Card bg="white" borderRadius="lg" boxShadow="md">
                <CardBody>
                    <EmptyStateCard
                        icon={FaChartLine}
                        title="Platform impact is not available yet"
                        description="This buyer view needs customer insight, RM coverage, and workflow data before it can show a meaningful summary."
                        hint="Try refreshing after scoring, RM assignment, or tracked who-to-call actions."
                        borderColor="brand.divider"
                        titleColor="gray.700"
                        descriptionColor="gray.600"
                        hintColor="gray.500"
                        iconColor="brand.teal"
                        maxW="full"
                    >
                        <Button leftIcon={<RepeatIcon />} colorScheme="blue" onClick={fetchPlatformImpact}>
                            Refresh impact
                        </Button>
                    </EmptyStateCard>
                </CardBody>
            </Card>
        );
    }

    return (
        <Container maxW="container.xl" py={6}>
            <VStack spacing={6} align="stretch">
                <Box>
                    <Heading size="lg" color="brand.navy" mb={2}>
                        Platform Impact
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                        Buyer-friendly snapshot of coverage, actionable contacts, RM workflow adoption, and customer pressure points.
                    </Text>
                </Box>

                {bookImpact ? <BookImpactView payload={bookImpact} /> : null}

                {impactData.isSyntheticDemo ? (
                    <SyntheticPreviewBanner message="No tenant data is available yet, so this page is showing a full synthetic demo sample." />
                ) : impactData.actionsAreSynthetic ? (
                    <SyntheticPreviewBanner message="No RM actions have been logged in this tenant yet, so the workflow, activity, and action-status sections below are a synthetic sample. Coverage and customer counts are live tenant data." />
                ) : null}

                <Card bg="white" borderRadius="lg" boxShadow="md">
                    <CardBody>
                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                            <Text fontSize="sm" color="gray.600">
                                Last updated: {lastUpdatedAt ? lastUpdatedAt.toLocaleString() : 'Never'}
                            </Text>
                            <Button
                                size="sm"
                                leftIcon={<RepeatIcon />}
                                colorScheme="blue"
                                bg="brand.navy"
                                onClick={fetchPlatformImpact}
                                _hover={{ bg: 'brand.navy2' }}
                            >
                                Refresh impact
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>

                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Card bg="white" borderRadius="xl" boxShadow="sm">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2} mb={1}>
                                    <Icon as={FaPhone} color="brand.teal" />
                                    <StatLabel color="gray.600">Qualified Contacts</StatLabel>
                                </HStack>
                                <StatNumber color="brand.navy">{Number(impactData.qualifiedContacts).toLocaleString('en-US')}</StatNumber>
                                <StatHelpText>High or medium urgency: top 20% of each adviser's book</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="xl" boxShadow="sm">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2} mb={1}>
                                    <Icon as={FaUserCheck} color="green.500" />
                                    <StatLabel color="gray.600">Assigned Coverage</StatLabel>
                                </HStack>
                                <StatNumber color="green.600">
                                    {impactData.totalCustomers > 0
                                        ? `${Math.round((impactData.assignedCustomers / impactData.totalCustomers) * 100)}%`
                                        : '0%'}
                                </StatNumber>
                                <StatHelpText>{Number(impactData.assignedCustomers).toLocaleString('en-US')} of {Number(impactData.totalCustomers).toLocaleString('en-US')} customers mapped to an RM</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="xl" boxShadow="sm">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2} mb={1}>
                                    <Icon as={FaExclamationTriangle} color="orange.500" />
                                    <StatLabel color="gray.600">Urgent Now</StatLabel>
                                </HStack>
                                <StatNumber color="orange.600">{Number(impactData.urgentCustomers).toLocaleString('en-US')}</StatNumber>
                                <StatHelpText>High urgency: top 5% of each adviser's book</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="xl" boxShadow="sm">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2} mb={1}>
                                    <Icon as={FaChartLine} color="purple.500" />
                                    <StatLabel color="gray.600">Avg RM Response Cycle</StatLabel>
                                </HStack>
                                <StatNumber color="purple.600">
                                    {impactData.avgResponseCycleDays > 0 ? impactData.avgResponseCycleDays.toFixed(1) : '0.0'}d
                                </StatNumber>
                                <StatHelpText>Proxy from tracked RM action updates</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Heading size="md" color="gray.700">Cluster Distribution</Heading>
                                    <Text fontSize="sm" color="gray.600">
                                        Current customer concentration by behavioural segment.
                                    </Text>
                                </Box>
                                <Box h="320px">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={impactData.segmentDistribution} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                            <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                                            <YAxis />
                                            <RechartsTooltip formatter={(value, name, props) => [`${value} customers`, `${props.payload.percentage?.toFixed(1) || 0}%`]} />
                                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                {impactData.segmentDistribution.map((entry, index) => (
                                                    <Cell key={entry.name} fill={entry.color || SEGMENT_COLORS[index % SEGMENT_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Heading size="md" color="gray.700">Workflow Conversion</Heading>
                                    <Text fontSize="sm" color="gray.600">
                                        How the RM workflow is moving from surfaced opportunities to logged action.
                                    </Text>
                                </Box>
                                <Box h="320px">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={impactData.pipelineData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <RechartsTooltip formatter={(value) => [`${value}`, 'Customers']} />
                                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                {impactData.pipelineData.map((entry, index) => (
                                                    <Cell key={entry.name} fill={entry.color || PIPELINE_COLORS[index % PIPELINE_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Heading size="md" color="gray.700">Recent RM Activity</Heading>
                                    <Text fontSize="sm" color="gray.600">
                                        Last 7 days of logged, completed, and deferred workflow activity.
                                    </Text>
                                </Box>
                                <Box h="320px">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={impactData.recentActivityData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                            <XAxis dataKey="name" />
                                            <YAxis allowDecimals={false} />
                                            <RechartsTooltip />
                                            <Bar dataKey="logged" fill="#0B2545" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="completed" fill="#38A169" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="deferred" fill="#DD6B20" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Heading size="md" color="gray.700">Action Status Mix</Heading>
                                    <Text fontSize="sm" color="gray.600">
                                        Operational mix of the RM follow-up workflow captured so far.
                                    </Text>
                                </Box>
                                <Box h="320px">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={impactData.actionStatusData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                            <XAxis dataKey="name" />
                                            <YAxis allowDecimals={false} />
                                            <RechartsTooltip formatter={(value) => [`${value}`, 'Actions']} />
                                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                {impactData.actionStatusData.map((entry, index) => (
                                                    <Cell key={entry.name} fill={entry.color || STATUS_COLORS[index % STATUS_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                <Card bg="white" borderRadius="lg" boxShadow="md">
                    <CardBody>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Heading size="md" color="gray.700">Weekly Admin Digest</Heading>
                                <Text fontSize="sm" color="gray.600">
                                    Fast readout of execution quality across the last 7 days of RM workflow activity.
                                </Text>
                            </Box>
                            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4}>
                                <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color="gray.600">Customers Touched</StatLabel>
                                            <StatNumber color="brand.navy">{impactData.weeklySummary.touchedCustomers}</StatNumber>
                                            <StatHelpText>{impactData.weeklySummary.coverageRate}% of surfaced pipeline covered</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color="gray.600">Completion Rate</StatLabel>
                                            <StatNumber color="green.600">{impactData.weeklySummary.completionRate}%</StatNumber>
                                            <StatHelpText>{impactData.weeklySummary.completedActions} actions completed this week</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color="gray.600">Meetings Booked</StatLabel>
                                            <StatNumber color="purple.600">{impactData.weeklySummary.meetingsBooked}</StatNumber>
                                            <StatHelpText>{impactData.weeklySummary.meetingRate}% of weekly logged actions</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color="gray.600">Overdue Follow-ups</StatLabel>
                                            <StatNumber color="orange.600">{impactData.weeklySummary.overdueFollowUps}</StatNumber>
                                            <StatHelpText>{impactData.weeklySummary.openBacklog} actions still open in backlog</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color="gray.600">Active RMs</StatLabel>
                                            <StatNumber color="teal.600">{impactData.weeklySummary.activeRms}</StatNumber>
                                            <StatHelpText>{impactData.weeklySummary.deferredActions} actions deferred in 7 days</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            </SimpleGrid>
                        </VStack>
                    </CardBody>
                </Card>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2}>
                                    <Icon as={FaUsers} color="brand.teal" />
                                    <StatLabel color="gray.600">Average Risk Profile</StatLabel>
                                </HStack>
                                <StatNumber color="brand.navy">{impactData.avgRiskProfile.toFixed(1)} / 10</StatNumber>
                                <StatHelpText>Current base-line average across the uploaded book</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2}>
                                    <Icon as={FaExclamationTriangle} color="red.500" />
                                    <StatLabel color="gray.600">High-Risk Customers</StatLabel>
                                </HStack>
                                <StatNumber color="red.600">{impactData.highRiskCustomers}</StatNumber>
                                <StatHelpText>Risk profile 8+ in the current customer base</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <Stat>
                                <HStack spacing={2}>
                                    <Icon as={FaUserTie} color="purple.500" />
                                    <StatLabel color="gray.600">Appointments Logged</StatLabel>
                                </HStack>
                                <StatNumber color="purple.600">{impactData.appointmentsLogged}</StatNumber>
                                <StatHelpText>Meeting actions captured from who-to-call follow-up</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                <Card bg="white" borderRadius="lg" boxShadow="md">
                    <CardBody>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Heading size="md" color="gray.700">RM Opportunity Coverage</Heading>
                                <Text fontSize="sm" color="gray.600">
                                    Which RMs currently hold the largest actionable pipeline.
                                </Text>
                            </Box>
                            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4}>
                                {impactData.rmCoverage.map((rm) => (
                                    <Card key={rm.name} bg="gray.50" borderRadius="md" boxShadow="sm">
                                        <CardBody>
                                            <VStack align="start" spacing={1}>
                                                <Text fontWeight="bold" color="gray.700" noOfLines={2}>{rm.name}</Text>
                                                <Text fontSize="sm" color="gray.600">{Number(rm.customers).toLocaleString('en-US')} assigned customers</Text>
                                                <Text fontSize="sm" color="brand.navy" fontWeight="semibold">
                                                    {Number(rm.qualified).toLocaleString('en-US')} qualified contacts
                                                </Text>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                ))}
                            </SimpleGrid>
                        </VStack>
                    </CardBody>
                </Card>

                <Card bg="white" borderRadius="lg" boxShadow="md">
                    <CardBody>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Heading size="md" color="gray.700">Weekly RM Execution Snapshot</Heading>
                                <Text fontSize="sm" color="gray.600">
                                    Short weekly digest for the admin on who is turning activity into completed follow-up.
                                </Text>
                            </Box>
                            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                                {impactData.weeklyWorkflowByRm.length > 0 ? (
                                    impactData.weeklyWorkflowByRm.map((rm) => (
                                        <Card key={`${rm.name}-weekly`} bg="gray.50" borderRadius="md" boxShadow="sm">
                                            <CardBody>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontWeight="bold" color="gray.700" noOfLines={2}>{rm.name}</Text>
                                                    <Text fontSize="sm" color="gray.600">{rm.weeklyLogged} logged this week</Text>
                                                    <Text fontSize="sm" color="green.600" fontWeight="semibold">
                                                        {rm.weeklyCompleted} completed ({rm.weeklyCompletionRate}%)
                                                    </Text>
                                                    <Text fontSize="sm" color="purple.600" fontWeight="semibold">
                                                        {rm.weeklyMeetings} meetings booked
                                                    </Text>
                                                    <Text fontSize="sm" color="orange.600" fontWeight="semibold">
                                                        {rm.overdue} overdue follow-ups
                                                    </Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : (
                                    <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                        <CardBody>
                                            <Text fontSize="sm" color="gray.600">
                                                Weekly execution cards will appear here as soon as RMs start tracking who-to-call actions.
                                            </Text>
                                        </CardBody>
                                    </Card>
                                )}
                            </SimpleGrid>
                        </VStack>
                    </CardBody>
                </Card>

                <Card bg="white" borderRadius="lg" boxShadow="md">
                    <CardBody>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Heading size="md" color="gray.700">RM Workflow Execution</Heading>
                                <Text fontSize="sm" color="gray.600">
                                    Top RMs by tracked workflow volume, with completion and defer counts.
                                </Text>
                            </Box>
                            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={4}>
                                {impactData.workflowByRm.length > 0 ? (
                                    impactData.workflowByRm.map((rm) => (
                                        <Card key={rm.name} bg="gray.50" borderRadius="md" boxShadow="sm">
                                            <CardBody>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontWeight="bold" color="gray.700" noOfLines={2}>{rm.name}</Text>
                                                    <Text fontSize="sm" color="gray.600">{rm.tracked} tracked actions</Text>
                                                    <Text fontSize="sm" color="brand.navy" fontWeight="semibold">{rm.completionRate}% completion rate</Text>
                                                    <Text fontSize="sm" color="green.600" fontWeight="semibold">{rm.completed} completed</Text>
                                                    <Text fontSize="sm" color="purple.600" fontWeight="semibold">{rm.meetings} meetings</Text>
                                                    <Text fontSize="sm" color="orange.600" fontWeight="semibold">{rm.deferred} deferred</Text>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : (
                                    <Card bg="gray.50" borderRadius="md" boxShadow="sm">
                                        <CardBody>
                                            <Text fontSize="sm" color="gray.600">
                                                RM workflow execution metrics will populate after the first tracked follow-up actions are created.
                                            </Text>
                                        </CardBody>
                                    </Card>
                                )}
                            </SimpleGrid>
                        </VStack>
                    </CardBody>
                </Card>
            </VStack>
        </Container>
    );
}

export default DashboardAdminPlatformImpact;
